import { type LDFlagSet } from "launchdarkly-react-client-sdk";
import React, { useMemo, useRef } from "react";

import { format } from "date-fns";
import { isEqual } from "lodash";

import PdfDocumentFooterReportV3 from "components/pdf-generation-v3/footer-v3/pdf-document-footer-report-v3.component";
import type { FormSettings } from "components/pdf-generation-v3/form-print.utils";
import PdfDocumentHeaderReport from "components/pdf-generation-v3/header/pdf-document-header-report";
import PdfDocument from "components/pdf-generation-v3/pdf-document.component";
import { PDF_PRINTABLE_TABLE_LAYOUT_FIXES } from "constants/feature-flags";
import { type Clinic } from "services/types/clinics.type";
import type { Patient } from "services/types/patients.type";
import type { MinimalStaffMember } from "services/types/staff.type";

export type CustomPrintFormPdfRenderProps = {
  clinic?: Clinic;
  content: string;
  staffMember?: MinimalStaffMember;
  // A license picked in the dialog for this form; absent, the signer's resolved license prints.
  signerLicenseNumber?: string;
  staffMemberSignature?: string;
  patient?: Patient;
  fileName: string;
  enableHeader: boolean;
  enablePatientSignature: boolean;
  enableProviderSignature: boolean;
  signature?: File | Blob;
  signedAt?: string | null;
  // ENG-2277: a Digital Form's footer slots are signed one by one: a date for a slot signed that
  // day, null for one nobody signed. Absent, both slots follow signedAt, as on any other form.
  patientSignedAt?: string | null;
  providerSignedAt?: string | null;
  dateOfVisit?: string | null;
  flags: LDFlagSet;
  isPdfNativeTablesEnabled?: boolean;
  // ENG-2277: a digital Form's print settings. Absent (or null), the page prints as it always has.
  formSettings?: FormSettings | null;
};

const DATE_MASK = "MM/dd/yyyy";

export const CustomPrintFormPdfRender: React.FC<
  CustomPrintFormPdfRenderProps
> = ({
  clinic,
  content,
  staffMember,
  signerLicenseNumber,
  patient,
  fileName,
  enableHeader,
  enablePatientSignature,
  enableProviderSignature,
  signature,
  staffMemberSignature,
  signedAt,
  patientSignedAt,
  providerSignedAt,
  dateOfVisit,
  flags,
  isPdfNativeTablesEnabled,
  formSettings
}) => {
  const signatureUrlRef = useRef<string | undefined>();
  const footer = useMemo(() => {
    const signedAtDate = signedAt
      ? format(new Date(signedAt), DATE_MASK)
      : undefined;

    // A slot's own date, for the footer: none to carry when the slot follows the footer's.
    const slotSignedAt = (ownSignedAt: string | null | undefined) =>
      ownSignedAt === undefined
        ? {}
        : {
            signedAtDate: ownSignedAt
              ? format(new Date(ownSignedAt), DATE_MASK)
              : null
          };

    const signatures = [];

    if (enableProviderSignature) {
      signatures.push({
        label: "Provider Signature",
        staffMember: {
          ...staffMember,
          signature: staffMemberSignature
        },
        licenseNumber:
          signerLicenseNumber ||
          staffMember?.resolved_license_number ||
          staffMember?.license_number ||
          undefined,
        ...slotSignedAt(providerSignedAt)
      });
    }

    if (enablePatientSignature) {
      if (signatureUrlRef.current) {
        URL.revokeObjectURL(signatureUrlRef.current);
      }
      if (signature) {
        signatureUrlRef.current = URL.createObjectURL(signature);
      }
      signatures.push({
        label: "Patient Signature",
        staffMember: {
          first_name: String(patient?.first_name),
          last_name: String(patient?.last_name),
          title: patient?.title ?? undefined,
          suffix: patient?.suffix,
          signature: signature ? URL.createObjectURL(signature) : undefined
        },
        ...slotSignedAt(patientSignedAt)
      });
    }
    if (!signatures.length) {
      return null;
    }
    return (
      <PdfDocumentFooterReportV3
        signedAtDate={signedAtDate}
        signatures={signatures}
      />
    );
  }, [
    enablePatientSignature,
    enableProviderSignature,
    patient?.first_name,
    patient?.last_name,
    patient?.suffix,
    patient?.title,
    staffMember,
    signerLicenseNumber,
    signature,
    signedAt,
    patientSignedAt,
    providerSignedAt,
    staffMemberSignature
  ]);

  if (!patient) return null;

  const dateOfVisitFormatted = dateOfVisit
    ? format(new Date(dateOfVisit), DATE_MASK)
    : "";

  const printableTableLayoutFixesEnabled = Boolean(
    flags[PDF_PRINTABLE_TABLE_LAYOUT_FIXES]
  );

  return (
    <PdfDocument
      title={fileName}
      pages={[
        {
          bodyHtml: `${content}`,
          pdfNativeTablesEnabled: isPdfNativeTablesEnabled,
          printableTableLayoutFixesEnabled,
          ...(formSettings && { formSettings }),
          header: enableHeader ? (
            <PdfDocumentHeaderReport
              clinic={clinic}
              dateOfVisit={dateOfVisitFormatted}
              patient={patient}
              staffMember={staffMember}
            />
          ) : undefined,

          footer
        }
      ]}
    />
  );
};

const MemoizedCustomPrintFormPdfRender = React.memo(
  CustomPrintFormPdfRender,
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  }
);

export default MemoizedCustomPrintFormPdfRender;
